import { useQuery } from "@tanstack/react-query";
import AppLayout from "@/components/app-layout";
import { type MarketResearch } from "@shared/schema";
import { TrendingUp, Building2, Search, DollarSign, Users, Clock, Trophy, ExternalLink, Flame, CheckCircle2, ChevronRight, Info } from "lucide-react";
import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";

type SortMode =
  | "searchToRoom"
  | "growthRate"
  | "searches"
  | "occupancy"
  | "priceShared"
  | "pricePrivate"
  | "daysFirstBooking"
  | "days80Occupancy";

const sortLabels: Record<SortMode, string> = {
  searchToRoom: "Search:Room Ratio",
  growthRate: "Growth Rate",
  searches: "Searches",
  occupancy: "Occupancy",
  priceShared: "Weekly Shared Rate",
  pricePrivate: "Weekly Private Rate",
  daysFirstBooking: "Fastest First Booking",
  days80Occupancy: "Fastest to 80%",
};

// Null-safe read of the Search:Room ratio. Returns null when the value is
// missing OR zero — a "0:1" ratio is never a real extracted value, only an
// artifact of a null import, so it must not rank or display as a number.
function getRatioValue(market: MarketResearch): number | null {
  const n = Number(market.searchToRoomRatio ?? 0);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function getSortValue(market: MarketResearch, mode: SortMode): number {
  switch (mode) {
    case "searchToRoom":
      // Missing ratios sort below every real ratio instead of tying at 0:1.
      return getRatioValue(market) ?? -1;
    case "growthRate": {
      const active = Number(market.totalUnits || 0);
      const upcoming = Number(market.upcomingUnits || 0);
      if (active === 0) return 0;
      return (upcoming / active) * 100;
    }
    case "searches":
      return Number(market.totalSearchVolume || 0);
    case "occupancy":
      return Number(market.occupancyRate || 0);
    case "priceShared":
      return Number(market.avgPriceShared || 0);
    case "pricePrivate":
      return Number(market.avgPricePrivate || 0);
    case "daysFirstBooking":
      return -(market.daysToFirstBooking ?? 999);
    case "days80Occupancy":
      return -(market.daysTo80Occupancy ?? 999);
    default:
      return 0;
  }
}

function formatSortValue(market: MarketResearch, mode: SortMode): string {
  switch (mode) {
    case "searchToRoom": {
      const ratio = getRatioValue(market);
      return ratio == null ? "—" : `${Math.round(ratio)}:1`;
    }
    case "growthRate": {
      const active = Number(market.totalUnits || 0);
      const upcoming = Number(market.upcomingUnits || 0);
      if (active === 0) return "0%";
      return `${((upcoming / active) * 100).toFixed(1)}%`;
    }
    case "searches":
      return Number(market.totalSearchVolume || 0).toLocaleString();
    case "occupancy":
      return `${market.occupancyRate}%`;
    case "priceShared":
      return `$${market.avgPriceShared}/wk`;
    case "pricePrivate":
      return `$${market.avgPricePrivate}/wk`;
    case "daysFirstBooking":
      return `${market.daysToFirstBooking} days`;
    case "days80Occupancy":
      return `${market.daysTo80Occupancy} days`;
    default:
      return "";
  }
}

function getRankColor(rank: number) {
  if (rank === 1) return "text-amber-600 dark:text-amber-300";
  if (rank === 2) return "text-gray-500 dark:text-gray-300";
  if (rank === 3) return "text-amber-800 dark:text-amber-200";
  return "text-gray-600 dark:text-gray-300";
}

function getRankBg(rank: number) {
  if (rank === 1) return "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/60";
  if (rank === 2) return "bg-gray-50 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700";
  if (rank === 3) return "bg-amber-100/60 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50";
  return "bg-white dark:bg-card border-gray-100 dark:border-border";
}

function computeBestValues(cityData: MarketResearch[]) {
  if (cityData.length === 0) return {};
  const best: Record<string, { city: string; value: number }> = {};

  const metrics: { key: string; field: (m: MarketResearch) => number; lower?: boolean }[] = [
    { key: "occupancy", field: (m) => Number(m.occupancyRate || 0) },
    { key: "searches", field: (m) => Number(m.totalSearchVolume || 0) },
    { key: "available", field: (m) => Number(m.availableUnits || 0) },
    { key: "activeUnits", field: (m) => Number(m.totalUnits || 0) },
    { key: "priceShared", field: (m) => Number(m.avgPriceShared || 0) },
    { key: "pricePrivate", field: (m) => Number(m.avgPricePrivate || 0) },
    { key: "daysFirst", field: (m) => m.daysToFirstBooking ?? 999, lower: true },
    { key: "days80", field: (m) => m.daysTo80Occupancy ?? 999, lower: true },
    { key: "ratio", field: (m) => getRatioValue(m) ?? 0 },
    { key: "growthRate", field: (m) => { const a = Number(m.totalUnits || 0); const u = Number(m.upcomingUnits || 0); return a === 0 ? 0 : (u / a) * 100; } },
  ];

  for (const metric of metrics) {
    let bestCity = cityData[0].city;
    let bestVal = metric.field(cityData[0]);
    for (let i = 1; i < cityData.length; i++) {
      const val = metric.field(cityData[i]);
      if (metric.lower ? val < bestVal : val > bestVal) {
        bestVal = val;
        bestCity = cityData[i].city;
      }
    }
    // A "best" of 0 means no city has a real value for this metric (e.g.
    // every ratio is missing) — highlighting the first city as a winner of a
    // universal tie at zero is misleading, so emit no winner instead.
    if (!metric.lower && bestVal <= 0) continue;
    best[metric.key] = { city: bestCity, value: bestVal };
  }
  return best;
}

export default function AppResearch() {
  usePageTitle("Market Research");
  const { data: markets = [], isLoading } = useQuery<MarketResearch[]>({
    queryKey: ["/api/app/market-research"],
  });

  const [, navigate] = useLocation();
  const [sortMode, setSortMode] = useState<SortMode>("searchToRoom");
  const [minRooms, setMinRooms] = useState<number>(1);

  const MIN_ROOMS_PRESETS = [1, 50, 100, 250, 500, 1000];

  const cityData = markets.filter((m) => m.level === "city");
  const filteredCityData = useMemo(
    () => cityData.filter((m) => Number(m.totalUnits || 0) >= minRooms),
    [cityData, minRooms]
  );

  const sorted = useMemo(() => {
    return [...filteredCityData].sort((a, b) => getSortValue(b, sortMode) - getSortValue(a, sortMode));
  }, [filteredCityData, sortMode]);

  const bestValues = useMemo(() => computeBestValues(filteredCityData), [filteredCityData]);

  const isBest = (city: string, metric: string) => bestValues[metric]?.city === city;

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48" />
            <div className="h-64 bg-gray-200 rounded" />
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-y-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="text-research-title" data-tour="research-heading">
              <TrendingUp className="h-6 w-6 text-blue-600" />
              Market Research
            </h1>
            <p className="text-sm text-gray-500 mt-1">PadSplit marketplace data ranked by demand</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Direct from PadSplit{markets[0]?.dataMonth ? ` — ${markets[0].dataMonth}` : ""}
            </div>
            <ExplainerVideoButton pageKey="research" />
            <GuideButton pageKey="research" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="research" />

        <div className="bg-white border border-gray-100 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" />
              City Rankings — {sortLabels[sortMode]}
            </h2>
          </div>

          <div className="flex flex-wrap gap-2 mb-4" data-testid="sort-controls" data-tour="research-markets">
            {(Object.keys(sortLabels) as SortMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setSortMode(mode)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-all cursor-pointer ${
                  sortMode === mode
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
                data-testid={`button-sort-${mode}`}
              >
                {sortLabels[mode]}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="min-rooms-filter">
            <span className="text-xs text-gray-500 font-medium">Min rooms:</span>
            {MIN_ROOMS_PRESETS.map((preset) => (
              <button
                key={preset}
                onClick={() => setMinRooms(preset)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-all cursor-pointer ${
                  minRooms === preset
                    ? "bg-gray-800 text-white border-gray-800"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
                data-testid={`button-min-rooms-${preset}`}
              >
                {preset}+
              </button>
            ))}
            <div className="relative group ml-1" data-testid="min-rooms-tooltip-trigger">
              <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 shadow-lg" data-testid="min-rooms-tooltip">
                Markets with very few rooms can produce misleading occupancy and search-to-room ratios. Set a minimum room threshold to focus on statistically meaningful markets.
              </div>
            </div>
          </div>

          {sortMode === "growthRate" && (
            <p className="text-xs text-gray-500 mb-4">
              Growth Rate = Upcoming Units / Active Units. Represents approximate monthly growth pace for each market.
            </p>
          )}
          {sortMode === "searchToRoom" && (
            <p className="text-xs text-gray-500 mb-4">
              Higher ratio = more demand per available room. Calculated as: Total Monthly Searches / Available Units.
            </p>
          )}

          {sorted.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-state-min-rooms">
              <Building2 className="h-10 w-10 text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-600 mb-1">No markets match this threshold</p>
              <p className="text-xs text-gray-400">Try lowering the minimum room count to see more markets.</p>
            </div>
          )}

          <div className="space-y-3">
            {sorted.map((market, index) => {
              const rank = index + 1;
              const isInverted = sortMode === "daysFirstBooking" || sortMode === "days80Occupancy";
              let barWidth: number;
              if (isInverted) {
                const bestDays = Math.abs(getSortValue(sorted[0], sortMode));
                const worstDays = Math.abs(getSortValue(sorted[sorted.length - 1], sortMode));
                const currentDays = Math.abs(getSortValue(market, sortMode));
                const range = worstDays - bestDays || 1;
                barWidth = Math.max(8, ((worstDays - currentDays) / range) * 92 + 8);
              } else {
                const sortVal = getSortValue(market, sortMode);
                const maxVal = Math.abs(getSortValue(sorted[0], sortMode)) || 1;
                barWidth = Math.max(8, (Math.abs(sortVal) / maxVal) * 100);
              }
              const growthRate = Number(market.totalUnits || 0) > 0
                ? ((Number(market.upcomingUnits || 0) / Number(market.totalUnits || 0)) * 100)
                : 0;

              return (
                <button
                  key={market.id}
                  onClick={() => {
                    const slug = `${market.city}-${market.state}`.toLowerCase().replace(/\s/g, "-");
                    navigate(`/app/research/${slug}`);
                  }}
                  className={`w-full text-left rounded-lg border p-4 transition-all cursor-pointer hover:shadow-md group ${getRankBg(rank)}`}
                  data-testid={`card-market-${market.city.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`text-2xl font-bold w-8 text-center ${getRankColor(rank)}`} data-testid={`text-rank-${market.city.toLowerCase().replace(/\s/g, "-")}`}>
                      {rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900" data-testid={`text-city-${market.city.toLowerCase().replace(/\s/g, "-")}`}>
                            {market.city}, {market.state}
                          </span>
                          {market.marketType && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">
                              {market.marketType}
                            </span>
                          )}
                        </div>
                        <span className="font-bold text-blue-700 text-lg" data-testid={`text-sort-value-${market.city.toLowerCase().replace(/\s/g, "-")}`}>
                          {formatSortValue(market, sortMode)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className="bg-blue-500 rounded-full h-2 transition-all duration-500"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                        <span className={`inline-flex items-center gap-1 ${isBest(market.city, "searches") ? "bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold" : ""}`}>
                          {Number(market.totalSearchVolume || 0).toLocaleString()} searches
                        </span>
                        <span>·</span>
                        <span className={`inline-flex items-center gap-1 ${isBest(market.city, "available") ? "bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold" : ""}`}>
                          {market.availableUnits?.toLocaleString()} available
                        </span>
                        <span>·</span>
                        <span className={`inline-flex items-center gap-1 ${isBest(market.city, "occupancy") ? "bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold" : ""}`}>
                          {market.occupancyRate}% occupancy
                        </span>
                        <span>·</span>
                        <span className={`inline-flex items-center gap-1 ${isBest(market.city, "activeUnits") ? "bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold" : ""}`}>
                          {Number(market.totalUnits || 0).toLocaleString()} active
                        </span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1">
                          {(market.upcomingUnits ?? 0).toLocaleString()} upcoming
                        </span>
                        <span>·</span>
                        <span className={`inline-flex items-center gap-1 ${isBest(market.city, "priceShared") ? "bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold" : ""}`}>
                          ${market.avgPriceShared}/wk shared
                        </span>
                        <span>·</span>
                        <span className={`inline-flex items-center gap-1 ${isBest(market.city, "pricePrivate") ? "bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold" : ""}`}>
                          ${market.avgPricePrivate}/wk private
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 flex-wrap">
                        <span className={`inline-flex items-center gap-1 ${isBest(market.city, "daysFirst") ? "bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold" : ""}`}>
                          <Clock className="h-3 w-3" />
                          {market.daysToFirstBooking}d to first book
                        </span>
                        <span>·</span>
                        <span className={`inline-flex items-center gap-1 ${isBest(market.city, "days80") ? "bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold" : ""}`}>
                          <Clock className="h-3 w-3" />
                          {market.daysTo80Occupancy}d to 80%
                        </span>
                        <span>·</span>
                        <span className={`inline-flex items-center gap-1 ${isBest(market.city, "growthRate") ? "bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold" : ""}`}>
                          <Flame className="h-3 w-3" />
                          {growthRate.toFixed(1)}% growth
                        </span>
                        <span>·</span>
                        <span className={`inline-flex items-center gap-1 ${isBest(market.city, "ratio") ? "bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold" : ""}`}>
                          {getRatioValue(market) == null ? "— ratio" : `${Math.round(getRatioValue(market)!)}:1 ratio`}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors shrink-0 ml-2" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-400 leading-relaxed" data-testid="text-research-disclaimer">
            * Market data sourced from PadSplit marketplace analytics for the month indicated. Metrics are approximate and subject to change. PadPulse makes no representations regarding the accuracy or completeness of this data. Search-to-available-room ratio is calculated as total monthly searches divided by estimated available units (total units × vacancy rate). Growth rate is calculated as upcoming units divided by active units, representing approximate monthly market expansion. This information is for research purposes only and should not be relied upon as the sole basis for investment decisions.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
